<template>
  <teleport to="body">
    <div
      class="fixed inset-0 z-[1000] bg-black/40 flex items-center justify-center p-4"
    >
      <div class="w-full max-w-sm bg-white rounded-2xl p-5 shadow-xl">
        <h3 class="text-lg font-semibold mb-1">Підтвердження номера</h3>
        <p class="text-sm text-gray-600 mb-4">
          <template v-if="sent">
            Ми надіслали SMS з кодом на
            <span class="font-medium">{{ masked }}</span
            >.
          </template>
          <template v-else>{{ throttleNote }}</template>
        </p>

        <div class="space-y-2">
          <label class="text-sm text-gray-600">Код з SMS</label>
          <input
            v-model="code"
            inputmode="numeric"
            maxlength="6"
            class="w-full border rounded-lg px-3 py-2"
            placeholder="••••••"
          />
          <p v-if="err" class="text-sm text-red-600">{{ err }}</p>
        </div>

        <div class="mt-4 flex gap-2">
          <button
            class="flex-1 px-4 py-2 rounded-xl bg-pink-200 hover:bg-pink-200/80 disabled:opacity-60"
            :disabled="confirming || sending || code.length < 6"
            @click="confirm"
          >
            {{ confirming ? "Перевіряємо…" : "Підтвердити" }}
          </button>
          <button
            type="button"
            class="px-4 py-2 rounded-xl border hover:bg-gray-50"
            :disabled="confirming || sending"
            @click="cancel"
          >
            Скасувати
          </button>
        </div>

        <!--
          Статичного контейнера reCAPTCHA тут більше НЕМАЄ. Він перевикористо-
          вувався між відправками, і iOS Safari віддавав «reCAPTCHA has already
          been rendered». Тепер контейнер створює і зносить канонічний
          композабл — свіжий off-screen на кожну відправку.
        -->

        <div class="mt-3 flex items-center justify-between gap-2">
          <button
            type="button"
            class="text-sm underline disabled:no-underline disabled:opacity-60"
            :disabled="confirming || sending || !otpAllowance.allowed"
            @click="sendCode"
          >
            {{ resendText }}
          </button>
          <span class="text-xs text-gray-500"
            >Залишилось спроб: {{ Math.max(0, MAX_ATTEMPTS - attempts) }}</span
          >
        </div>
      </div>
    </div>
  </teleport>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import { PhoneAuthProvider, updatePhoneNumber } from "firebase/auth";
import { useNuxtApp } from "#app";
import { allowOtpSend } from "~/utils/otpThrottle";
import { usePhoneOtp } from "~/composables/auth/usePhoneOtp";

const props = defineProps({
  phoneE164: { type: String, required: true },
  masked: { type: String, default: "" },
});
const emit = defineEmits(["verified", "cancel"]);

const { $auth } = useNuxtApp();

/** Скільки невдалих підтверджень терпимо, перш ніж закрити модалку. */
const MAX_ATTEMPTS = 3;

const code = ref("");
const err = ref("");

/**
 * Канонічний флоу. Уся механіка — верифаєр, гальмо, мапінг помилок і Sentry —
 * спільна з входом; різниця рівно у двох фінальних діях нижче.
 *
 * До цього тут жила друга копія всього: свій RecaptchaVerifier на статичному
 * контейнері, свій capture (читав самий `e.code`, тож тег `firebase_code`
 * часто був порожній) і ДВА захардкоджені тексти помилок замість мапінгу —
 * через них `auth/too-many-requests` виглядав як «Не вдалося надіслати код».
 */
const {
  sending,
  confirming,
  sent,
  attempts,
  sendCode: send,
  confirmCode,
  reset,
} = usePhoneOtp({
  flow: "phone_update",
  send: (phoneE164, verifier) =>
    new PhoneAuthProvider($auth).verifyPhoneNumber(phoneE164, verifier),
  confirm: async (verificationId, smsCode) => {
    const credential = PhoneAuthProvider.credential(verificationId, smsCode);
    await updatePhoneNumber($auth.currentUser, credential);
  },
});

// Секундний тікер лишається тут: текст зворотного відліку в модалці і на
// /login різний, і це презентація, а не механіка.
const nowTs = ref(Date.now());
let otpTicker = null;

const otpAllowance = computed(() => allowOtpSend(props.phoneE164, nowTs.value));

function waitText(allowance) {
  if (allowance.reason === "window") {
    const min = Math.max(1, Math.ceil(allowance.retryAfterSec / 60));
    return { short: `Ліміт вичерпано — через ${min} хв`, min };
  }
  return { short: `Надіслати ще раз (${allowance.retryAfterSec}с)`, min: 0 };
}

const throttleNote = computed(() => {
  const allowance = otpAllowance.value;
  if (allowance.allowed) return "Натисніть «Надіслати код», щоб отримати SMS.";
  if (allowance.reason === "window") {
    return `Забагато кодів на цей номер. Спробуйте за ${waitText(allowance).min} хв.`;
  }
  return `Наступний код можна надіслати через ${allowance.retryAfterSec} с.`;
});

const resendText = computed(() => {
  const allowance = otpAllowance.value;
  if (allowance.allowed)
    return sent.value ? "Надіслати ще раз" : "Надіслати код";
  return waitText(allowance).short;
});

async function sendCode() {
  err.value = "";
  try {
    await send(props.phoneE164);
  } catch (e) {
    // Гальмо — не помилка: `throttleNote` уже пояснює, скільки чекати, і
    // червоний текст поверх нього лише лякав би. Модалка колись слала SMS
    // просто на onMounted без жодного гальма: закрив-відкрив = ще одна SMS.
    if (e?.message !== "otp/throttled") {
      err.value =
        e?.userMessage || "Не вдалося надіслати код. Спробуйте пізніше.";
    }
  } finally {
    nowTs.value = Date.now();
  }
}

async function confirm() {
  err.value = "";
  try {
    await confirmCode(code.value);
    emit("verified");
  } catch (e) {
    err.value = e?.userMessage || "Код невірний або прострочений.";
    if (attempts.value >= MAX_ATTEMPTS) emit("cancel");
  }
}

function cancel() {
  reset();
  emit("cancel");
}

onMounted(() => {
  otpTicker = setInterval(() => {
    nowTs.value = Date.now();
  }, 1000);
  sendCode();
});

onBeforeUnmount(() => {
  if (otpTicker) clearInterval(otpTicker);
  reset();
});
</script>
